import Modal from '_/components/Modal';
import ModalDoubleButton from '_/components/ModalDoubleButton';
import ModalMeeting from '_/components/ModalMeeting';
import ModalPhoneNumberValidator from '_/components/ModalPhoneNumberValidator';
import ModalQRCodeCamera from '_/components/ModalQRCodeCamera';
import appConfig from '_/config/app';
import { AppRoute } from '_/navigation/types';
import { showMessage } from 'react-native-flash-message';
import Constants from 'expo-constants';
import CanceledModal from '../CanceledModal';
import { colors, fontSizes } from '_/config/theme';
import { Text } from '_/components';
import useVisitorCheckinStore from './useVisitorCheckinStore';
import { useAuth } from '_/hooks/AuthContext';
import { useCentralizedValuesFallback } from '_/hooks/useCentralizedValuesFallback';
import { useNavigation } from '@react-navigation/native';
import { useTranslation } from 'react-i18next';
import { usePrinters } from '_/hooks/PrintersContext';
import { useMemo, useState } from 'react';
import { useVisit } from '_/hooks/VisitContext';
import { checkVisitValidity } from '_/helpers/checkVisitValidity';
import { VisitsModelCheckin } from '_/interfaces/VisitsModelCheckin';
import { VisitAuthorizationStatusType } from '@w3lcome/types';

export interface VisitorCheckinModalsProps {
  meetingModalVisible: boolean;
  goToNextScreen: () => void;
}

export function VisitorCheckinModals({
  meetingModalVisible,
  goToNextScreen,
}: VisitorCheckinModalsProps) {
  const [
    isMeetingModalVisible,
    modalCheckVisitorNameVisible,
    verifyPhoneNumberModal,
    setVerifyPhoneNumberModal,
    setModalCheckVisitorNameVisible,
    setMeetingModalVisible,
    isQRCodeReaderVisible,
    setQRCodeReaderVisible,
  ] = useVisitorCheckinStore((state) => [
    state.isMeetingModalVisible,
    state.modalCheckVisitorNameVisible,
    state.verifyPhoneNumberModal,
    state.setModalCheckVisitorNameVisible,
    state.setVerifyPhoneNumberModal,
    state.setMeetingModalVisible,
    state.isQRCodeReaderVisible,
    state.setQRCodeReaderVisible,
  ]);

  const [isModalPrinterVisible, setModalPrinterVisible] = useState(false);
  const [errorModalMessage, setErrorModalMessage] = useState<string>();

  const { ipad } = useAuth();

  const { selectedOrFallbackCustomization, selectedOrFallbackCompany } =
    useCentralizedValuesFallback();

  const navigation = useNavigation<any>();

  const { t } = useTranslation();

const { printVisitBadge } = usePrinters();

  const {
    loading,
    setLoading,
    setVisitParser,
    setIsPrintVisitBadge,
    showMessageErrorUniqueVisit,
    resetData,
    visit,
    getVisitByQRCode,
    navigateToHome,
  } = useVisit();

  const checkCardNumberToPrintBadge = useMemo(() => {
    if (ipad?.printerId && selectedOrFallbackCompany?.id !== appConfig.companyIdSbt) {
      if (Constants.expoConfig?.extra?.checkCardNumberToPrintBadge === 'true') {
        if (visit.cardNumber) {
          return true;
        }
        return false;
      }
      return true;
    }

    return false;
  }, [visit, ipad, selectedOrFallbackCompany]);

  function hideModalMeeting(isMeetingConfirmed = true) {
    setMeetingModalVisible(false);

    if (!isMeetingConfirmed) resetData();
  }

  function handleConfirmVisitorName() {
    setModalCheckVisitorNameVisible(false);

    if (
      visit.isRecurrent &&
      selectedOrFallbackCustomization?.fields?.some((field) => field.alwaysRequireInput)
    ) {
      navigation.navigate(AppRoute.CHECKIN_INFORMATION);
      return;
    }

    goToNextScreen();
  }

  async function handleScannedQRCode(data: string) {
    setLoading(true);
    setQRCodeReaderVisible(false);
    try {
      const visit = await getVisitByQRCode(data);

      if (visit) {
        const isValidVisit = checkVisitValidity(
          visit as Partial<VisitsModelCheckin>,
          selectedOrFallbackCustomization
        );
        if (isValidVisit) {
          setVisitParser(visit);
          setMeetingModalVisible(true);
        } else {
          setErrorModalMessage(t('QRCodeAlreadyUsed'));
        }
      } else {
        setErrorModalMessage(t('InvalidQRCode'));
      }
    } catch (error) {
      setErrorModalMessage(t('InvalidQRCode'));
    }
    setLoading(false);
  }

  function handleRejectVisitorName() {
    setModalCheckVisitorNameVisible(false);
    resetData();
  }

  const uniqueMessage =
    visit.authorizationStatus === VisitAuthorizationStatusType.AWAITING
      ? selectedOrFallbackCustomization?.visitorFacialEnabled
        ? 'invenziFacialWarningAwaitingAuthorization'
        : 'invenziQRcodeWarningAwaitingAuthorization'
      : 'checkinScreen.uniqueVisit';

  return (
    <>
      {((meetingModalVisible && !visit?.isCanceled) ||
        (isMeetingModalVisible && !visit?.isCanceled)) && (
        <ModalMeeting
          isModalVisible={isMeetingModalVisible}
          showModalPrinter={() =>
            selectedOrFallbackCompany?.id !== appConfig.companyIdSbt
              ? setModalPrinterVisible(true)
              : goToNextScreen()
          }
          hideModalMeeting={hideModalMeeting}
        />
      )}

      {modalCheckVisitorNameVisible && visit.name && (
        <ModalDoubleButton
          isModalVisible={modalCheckVisitorNameVisible}
          onCloseText={t('No')}
          onFinishText={t('Yes')}
          onClose={() => handleRejectVisitorName()}
          onFinish={() => handleConfirmVisitorName()}
        >
          <Text size={fontSizes.xl}>{t('areYou', { name: visit.name })}</Text>
        </ModalDoubleButton>
      )}

      {visit?.isCanceled && <CanceledModal />}

      <ModalQRCodeCamera
        cameraType={ipad?.settingsQRCodeCameraPosition.toLowerCase() as any}
        isScannerEnabled={
          !loading && !errorModalMessage && !isMeetingModalVisible && isQRCodeReaderVisible
        }
        isVisible={isQRCodeReaderVisible}
        onClose={() => setQRCodeReaderVisible(false)}
        onScan={handleScannedQRCode}
      />

      {showMessageErrorUniqueVisit && (
        <>
          {checkCardNumberToPrintBadge ? (
            <ModalDoubleButton
              message={t(uniqueMessage)}
              messageBold={t('checkinScreen.printQuestion')}
              isModalVisible={showMessageErrorUniqueVisit}
              onCloseText={t('checkinScreen.Back')}
              onFinishText={t('checkinScreen.reprintTheLabel')}
              onClose={() => {
                navigateToHome(navigation);
              }}
              onFinish={async () => {
                await printVisitBadge({ ...visit, checkinAt: new Date() });
                showMessage({
                  duration: 6000,
                  message: `${t('checkinScreen.printing')}...`,
                  description: t('checkinScreen.waitPrint'),
                  backgroundColor: colors.warning,
                });
                navigateToHome(navigation);
              }}
            />
          ) : (
            <Modal
              icon="alert-circle"
              iconColor={colors.danger}
              message={t(uniqueMessage)}
              isVisible={showMessageErrorUniqueVisit}
              onClose={() => navigateToHome(navigation)}
            />
          )}
        </>
      )}

      {isModalPrinterVisible && (
        <ModalDoubleButton
          title={`${visit?.name || t('Hello')}, ${t('checkinScreen.print')}`}
          onCloseText={t('No')}
          onFinishText={t('Yes')}
          onClose={() => {
            setIsPrintVisitBadge(false);
            setModalPrinterVisible(false);
            goToNextScreen();
          }}
          onFinish={() => {
            setIsPrintVisitBadge(true);
            setModalPrinterVisible(false);
            goToNextScreen();
          }}
          isModalVisible={isModalPrinterVisible}
        />
      )}

      {verifyPhoneNumberModal && (
        <ModalPhoneNumberValidator
          isModalVisible={verifyPhoneNumberModal}
          closeModal={setVerifyPhoneNumberModal}
          successModal={() => navigation.navigate(AppRoute.CHECKIN_INFORMATION)}
        />
      )}

      <Modal
        icon="alert-circle"
        iconColor={colors.danger}
        message={errorModalMessage}
        isVisible={!!errorModalMessage}
        onClose={() => setErrorModalMessage(undefined)}
      />
    </>
  );
}
